import React, { useState } from 'react';
import { Heading, Button, Badge, TextStyle } from '@superops/ui-design-system';
import { FormattedMessage } from 'react-intl';
import {
  SuperTable,
  TableContainer,
  getTranslatedText
} from '@nile/components';
import { SVGIcon } from '@superops/super-icons';
import { MdmPolicyContainerProps, WifiConfiguration } from '../types/interface';
import WifiModal from './WifiModal';
import WarningDialog from './WarningDialog';
import { Policy } from '../types/interface';
import PolicyTitle from './PolicyTitle';
import { DisplayText } from '@superops/ui-design-system';
import envConfig from '@nile/home/src/Config/env';

interface MDMWifiProps extends MdmPolicyContainerProps {
  wifiConfigurations: WifiConfiguration[];
  onWifiConfigurationsChange: (configs: WifiConfiguration[]) => void;
  onRemovedWifiIds: (ids: { configId: string }[]) => void;
  policyObj?: Policy;
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  disabled?: boolean;
  isReadOnly?: boolean;
  wifiDescription?: string;
}

function MDMWifi(props: MDMWifiProps) {
  const {
    intl,
    classId,
    policyObj: policy,
    wifiConfigurations,
    onWifiConfigurationsChange,
    onRemovedWifiIds,
    enabled = true,
    onEnabledChange,
    disabled = false,
    isReadOnly = false,
    wifiDescription = ''
  } = props;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWifiModal, setShowWifiModal] = useState(false);
  const [selectedWifi, setSelectedWifi] = useState<WifiConfiguration | null>(
    null
  );

  const handleAddWifi = () => {
    setSelectedWifi(null);
    setShowWifiModal(true);
  };

  const handleEditWifi = (wifi: WifiConfiguration) => {
    setSelectedWifi(wifi);
    setShowWifiModal(true);
  };

  const handleDeleteWifi = (wifi: WifiConfiguration) => {
    setSelectedWifi(wifi);
    setShowDeleteModal(true);
  };

  const handleSaveWifi = (
    wifi: WifiConfiguration,
    isEdit: boolean,
    originalWifi?: WifiConfiguration
  ) => {
    // When policy is inherited, always treat WiFi configs as new items
    const isInheritedPolicy = policy?.inherited;

    if (isEdit && originalWifi && !isInheritedPolicy) {
      // Update existing WiFi (only for non-inherited policies)
      const updatedWifiConfigurations = wifiConfigurations.map((w) =>
        w.configId === originalWifi.configId ? wifi : w
      );
      onWifiConfigurationsChange(updatedWifiConfigurations);
    } else if (isEdit && originalWifi && isInheritedPolicy) {
      // For inherited policies, replace the config but mark as new item
      const updatedWifiConfigurations = wifiConfigurations.map((w) =>
        w.configId === originalWifi.configId ? { ...wifi, isNewItem: true } : w
      );
      onWifiConfigurationsChange(updatedWifiConfigurations);
    } else {
      // Add new WiFi
      const newWifiConfigurations = [
        ...wifiConfigurations,
        { ...wifi, isNewItem: true }
      ];
      onWifiConfigurationsChange(newWifiConfigurations);
    }

    setShowWifiModal(false);
    setSelectedWifi(null);
  };

  const handleCloseModal = () => {
    setShowWifiModal(false);
    setSelectedWifi(null);
  };

  const confirmDelete = async () => {
    if (!selectedWifi) return;

    try {
      // Remove from configurations
      onWifiConfigurationsChange(
        wifiConfigurations.filter(
          (wifi) => wifi.configId !== selectedWifi.configId
        )
      );

      // When policy is inherited, don't call removeWifi API for inherited configs
      const isInheritedPolicy = policy?.inherited;

      // If it has a real configId from API (not temporary), notify parent for API deletion
      // But skip API deletion for inherited policies
      if (
        !selectedWifi.isNewItem &&
        !selectedWifi.configId.startsWith('temp_') &&
        !isInheritedPolicy
      ) {
        onRemovedWifiIds([{ configId: selectedWifi.configId }]);
      }

      setShowDeleteModal(false);
      setSelectedWifi(null);
    } catch (error) {
      console.error('Failed to delete WiFi configuration:', error);
    }
  };

  const SSIDCell = ({ row }: any) => {
    return (
      <Button plain onClick={() => handleEditWifi(row.original)}>
        <TextStyle weight="SemiBold">{row.original.ssid}</TextStyle>
      </Button>
    );
  };

  const PasswordCell = ({ row }: any) => {
    const [isVisible, setIsVisible] = useState(false);
    const password = row.original.wifiPassword;
    if (!password)
      return (
        <div className="flex actr ml-10">
          <TextStyle variation="subdued">
            {getTranslatedText(intl, 'app.noData.message')}
          </TextStyle>
        </div>
      );
    const maskedPassword = '*'.repeat(password.length);

    return (
      <div className="flex actr visibilityContainer ml-10">
        <span className="mr-8">{isVisible ? password : maskedPassword}</span>
        <button
          className="PlainButtonStyle visibilityActions"
          onClick={() => setIsVisible(!isVisible)}
        >
          <SVGIcon name={isVisible ? 'EyeVisible' : 'EyeHidden'} />
        </button>
      </div>
    );
  };

  const AutoConnectCell = ({ cell }: any) => {
    return (
      <div className="ml-10">
        <Badge>
          <TextStyle weight="Regular" uppercase>
            {cell.getValue()
              ? getTranslatedText(intl, 'common.enabled')
              : getTranslatedText(intl, 'common.disabled')}
          </TextStyle>
        </Badge>
      </div>
    );
  };

  const ActionsCell = ({ row }: any) => {
    return (
      <div className="flex jcenter ml-10">
        <span className="fx jend">
          <button
            className="PlainButtonStyle"
            onClick={() => handleDeleteWifi(row.original)}
          >
            <SVGIcon name="Delete" color="red" />
          </button>
        </span>
      </div>
    );
  };

  const TextCell = ({ cell }: any) => {
    return (
      <div className="ml-10">
        <TextStyle weight="Regular">{cell.getValue()}</TextStyle>
      </div>
    );
  };

  const columnsConfig = {
    minWidth: 30,
    maxWidth: 500,
    width: 300,
    TEXT: TextCell,
    SSID: SSIDCell,
    PASSWORD: PasswordCell,
    AUTOCONNECT: AutoConnectCell,
    ACTIONS: ActionsCell
  };

  const columns = [
    {
      header: getTranslatedText(intl, 'mdm.wifi.name'),
      accessorKey: 'ssid',
      id: 'ssid',
      type: 'SSID',
      size: 180,
      enableResizing: false,
      disableSortBy: true,
      disableFilters: true
    },
    {
      header: getTranslatedText(intl, 'mdm.wifi.auto.connect'),
      accessorKey: 'autoJoin',
      id: 'autoJoin',
      type: 'AUTOCONNECT',
      size: 130,
      enableResizing: false,
      disableSortBy: true,
      disableFilters: true
    },
    {
      header: getTranslatedText(intl, 'mdm.wifi.security.type'),
      accessorKey: 'encryptionType',
      id: 'encryptionType',
      type: 'TEXT',
      size: 130,
      enableResizing: false,
      disableSortBy: true,
      disableFilters: true
    },
    {
      header: getTranslatedText(intl, 'common.password'),
      accessorKey: 'wifiPassword',
      id: 'wifiPassword',
      type: 'PASSWORD',
      size: 150,
      enableResizing: false,
      disableSortBy: true,
      disableFilters: true
    },
    {
      header: '',
      accessorKey: 'actions',
      id: 'actions',
      type: 'ACTIONS',
      size: 50,
      enableResizing: false,
      disableSortBy: true,
      disableFilters: true
    }
  ];

  return (
    <div className="mdm-wifi-container">
      <div className="mb-20">
        <PolicyTitle
          isReadOnly={isReadOnly}
          policyName="mdm.policy.network.wifi.label"
          policy={policy || null}
          enabled={enabled}
          onEnabledChange={onEnabledChange}
          disabled={disabled}
          showToggle={true}
          isTitle
        />
        {wifiDescription && (
          <DisplayText element="p" size="medium" variant="subdued">
            {wifiDescription}
          </DisplayText>
        )}
      </div>
      <div className="flex actr space-between mb-20">
        <Heading element="h3" weight="SemiBold">
          <FormattedMessage id="label.configurations" />
        </Heading>
        <Button
          onClick={handleAddWifi}
          variant="primary"
          icon={<SVGIcon name="Add" />}
        >
          <FormattedMessage id="common.add" />
        </Button>
      </div>

      {wifiConfigurations.length === 0 ? (
        <div className="flex actr g20">
          <img
            src={`${envConfig.imageRoot}svg/empty-software-bundle.svg`}
            alt="Empty wifi configurations"
            height={120}
            width={120}
          />
          <TextStyle variation="subdued">
            <FormattedMessage id="mdm.no.config.found" />
          </TextStyle>
        </div>
      ) : (
        <TableContainer>
          <SuperTable
            noBulkSelect
            context={{
              records: [],
              setRecords: () => {},
              viewConditions: ''
            }}
            columns={[{ Header: 'Name', header: 'name', columns: columns }]}
            data={wifiConfigurations}
            defaultColumnCells={columnsConfig}
            skipPageReset
            pagination={false}
            fullHeight={false}
            padded="lg"
          />
        </TableContainer>
      )}

      <WarningDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onConfirm={confirmDelete}
        title={getTranslatedText(intl, 'common.warning')}
        message={getTranslatedText(intl, 'mdm.wifi.delete.dialog.message', {
          ssid: selectedWifi?.ssid
        })}
        confirmText={getTranslatedText(intl, 'common.delete')}
        cancelText={getTranslatedText(intl, 'common.cancel')}
        variant="confirmation"
        confirmDestructive={true}
        confirmIcon={<SVGIcon name="Delete" />}
      />

      <WifiModal
        intl={intl}
        isOpen={showWifiModal}
        onClose={handleCloseModal}
        onSave={handleSaveWifi}
        classId={classId}
        selectedWifi={selectedWifi}
        policyObj={policy}
      />
    </div>
  );
}

export default MDMWifi;
